import React, {
  FC, Ref, useMemo,
} from 'react';
import classNames from 'classnames';
import { Trans, useTranslation } from 'next-i18next';
import { trackEvent } from 'src/common/3rdParty/tracking';
import { apiRequest } from 'src/common/utils/apiFetch';
import getSnoozeStatus from 'src/common/utils/getSnoozeStatus';
import { ListingCardLocation } from 'src/common/types/common/ListingCard';
import { DateString, getNextDay } from 'src/common/utils/dateHelpers';
import getYearOfBestOfAward from 'src/common/utils/getYearOfBestOfAward';
import { Breadcrumb, Photo } from 'src/common/types/api/v4/boats/search';
import UniversalLink from '../UniversalLink';
import ListingCardImage from '../ListingCardImage';

import getClassNames from '../../utils/getClassNames';
import ListingCardOverview from '../ListingCardOverview';
import ListingBadge from '../ListingBadge';
import WithOverlay from '../WithOverlay';
import {
  BOOKING_MOTIVATOR_STATES,
  NEW_TAB_ATTRIBUTES,
  SEARCH_INSTABOOK_GTAG_EVENT,
  HOMEPAGE_INSTABOOK_GTAG_EVENT,
  DLP_INSTABOOK_GTAG_EVENT,
} from '../../constants';
import styles from './ListingCard.module.scss';

type Link = {
  url: string;
  heading: string;
  testMark: string;
  onClick: () => void;
  target?: string;
  rel?: string;
};

const { LIMITED_AVAILABILITY, HIGH_DEMAND } = BOOKING_MOTIVATOR_STATES;

const i18nKeysMap = {
  [LIMITED_AVAILABILITY]: 'listing-card--limited-availability',
  [HIGH_DEMAND]: 'listing-card--high-demand',
} as const;

type I18nKey = keyof typeof i18nKeysMap;

const highlightedListingClickUrl = '/api/v4/boats/update_highlighted_listing_clicks/';

const updateHighlightedListingClicks = (listingId: string) => {
  apiRequest(highlightedListingClickUrl, {
    method: 'POST',
    body: JSON.stringify({
      boat_id: listingId,
      clicks: 1,
    }),
  });
};

const trackListingCardInteraction = (
  listingId: string,
  isStructured: boolean,
  isHighlighted: boolean,
  gtagEventListingCard?: string,
) => {
  let action = isStructured ? 'Carousel Item Clicked' : 'Search Item Clicked';
  const gtagConfig = {
    event_category: 'Search',
    event_label: listingId,
    non_interaction: false,
  };
  if (isHighlighted) {
    updateHighlightedListingClicks(listingId);
    trackEvent('Highlighted Search Item Clicked', gtagConfig);
  }
  if (gtagEventListingCard) {
    action = gtagEventListingCard;
  }
  trackEvent(action, gtagConfig);
};

export const ALLOWED_MOTIVATORS = [LIMITED_AVAILABILITY, HIGH_DEMAND] as const;
type Motivators = typeof ALLOWED_MOTIVATORS[number];
export type ListingCardVariants = 'popup' | 'favorites' | 'carouselItem' | 'preview' | 'homepage' | 'instabook';
export type ListingCardGtagEvent = typeof SEARCH_INSTABOOK_GTAG_EVENT
  | typeof HOMEPAGE_INSTABOOK_GTAG_EVENT
  | typeof DLP_INSTABOOK_GTAG_EVENT;

/**
 * Determine if the supplied Motivator is on the "Allowed" list. This prevents an
 * empty ListingBadge from displaying.
 */
const isAllowedMotivator = (x: any): x is Motivators => ALLOWED_MOTIVATORS.includes(x);

export type ListingCardProps = {
  listingId?: string;
  cardRef?: Ref<HTMLDivElement>;
  variant?: ListingCardVariants;
  listingUrl?: string;
  imageSrc: string;
  capacity?: string;
  headline: string;
  rating?: number;
  isSuperOwner?: boolean;
  isActive?: boolean;
  isSelected?: boolean;
  breadcrumbs?: Breadcrumb[];
  category?: Breadcrumb | null;
  awards?: string[];
  instantBookingAvailable?: boolean;
  inquirySent?: boolean;
  asCarouselItem?: boolean;
  openInNewTab?: boolean;
  bookingMotivator?: Motivators;
  isHighlighted?: boolean;
  searchPricing?: {
    price_unit: string;
    formatted: string;
  };
  /**
   * Define how the images should load. Defaults to `lazy`
   */
  loading?: 'lazy' | 'eager';
  /**
   * Make use of the new fetchpriority property on images
   * https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/fetchPriority
   */
  fetchpriority?: 'high' | 'low' | null;
  photos?: Photo[];
  cardLocation?: ListingCardLocation;
  gtagEvent?: ListingCardGtagEvent;
  snoozeStartDate?: DateString;
  snoozeEndDate?: DateString;
  withFavorite?: boolean;
  onClick?: () => void;
};

const ListingCard: FC<ListingCardProps> = ({
  listingId,
  cardRef,
  variant,
  listingUrl,
  imageSrc,
  capacity,
  headline,
  rating,
  isSuperOwner,
  isActive,
  isSelected,
  breadcrumbs,
  category,
  loading,
  fetchpriority,
  awards,
  instantBookingAvailable,
  photos,
  inquirySent = false,
  asCarouselItem = false,
  openInNewTab = false,
  bookingMotivator,
  isHighlighted,
  searchPricing = {},
  gtagEvent: gtagEventListingCard,
  cardLocation,
  snoozeStartDate,
  snoozeEndDate,
  withFavorite = true,
  onClick,
}) => {
  const [t] = useTranslation('common');
  const isPopup: boolean = variant === 'popup';
  const isPreview: boolean = variant === 'preview' || !!onClick;

  const { snoozeActive } = useMemo(
    () => getSnoozeStatus(snoozeStartDate, snoozeEndDate, true),
    [snoozeEndDate, snoozeStartDate],
  );
  const formattedSnoozeEndDate = useMemo(() => getNextDay(snoozeEndDate), [snoozeEndDate]);

  const bestOfYear = useMemo(() => getYearOfBestOfAward(awards), [awards]);
  const isUnpublished = !listingUrl && !isPreview;
  const modifiers = useMemo(
    () => classNames(variant, { active: isActive, isUnpublished, selected: isSelected }),
    [variant, isActive, isUnpublished, isSelected],
  );

  const city: string = breadcrumbs && breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1]?.name : '';
  const heading: string = category?.name && city
    ? t('listing-card-overview--heading', { boatType: category?.name, city })
    : category?.name ?? city;

  let link: Link | null = null;
  if (listingUrl && !isPreview) {
    link = {
      url: listingUrl,
      heading,
      testMark: 'ListingCard',
      onClick: () => trackListingCardInteraction(
        listingId,
        asCarouselItem,
        isHighlighted,
        gtagEventListingCard,
      ),
      ...(openInNewTab ? NEW_TAB_ATTRIBUTES : {}),
    };
  }

  const img = (
    <ListingCardImage
      isPopup={isPopup}
      isPreview={isPreview}
      photos={photos}
      src={imageSrc}
      alt={headline}
      loading={loading}
      fetchpriority={fetchpriority}
      link={link || undefined}
      onLinkClick={link && link.onClick}
    />
  );

  const motivatorVariantMap = {
    [HIGH_DEMAND]: 'motivator-high',
    [LIMITED_AVAILABILITY]: 'motivator-limited',
  } as const;

  return (
    <div
      className={getClassNames(styles, 'root', modifiers)}
      data-gulper-item={listingId}
    >
      {bookingMotivator && isAllowedMotivator(bookingMotivator) && !isPopup && (
        <div className={styles.motivator}>
          <ListingBadge
            variant={motivatorVariantMap[bookingMotivator]}
          >
            {t(i18nKeysMap[bookingMotivator as I18nKey])}
          </ListingBadge>
        </div>
      )}
      {link && (
        <UniversalLink
          href={link.url}
          aria-label={link.heading}
          data-test={link.testMark}
          className={getClassNames(styles, 'link', modifiers)}
          onClick={link.onClick}
          target={link?.target}
          rel={link?.rel}
        />
      )}
      {onClick && (
        <button
          type="button"
          className={getClassNames(styles, 'button', modifiers)}
          onClick={onClick}
          aria-label={heading}
        />
      )}
      <div
        ref={cardRef}
        className={getClassNames(styles, 'contents', modifiers)}
      >
        <div className={styles.imageContainer}>
          {(isUnpublished || inquirySent) && (
            <WithOverlay
              text={isUnpublished ? t('listing-card--unpublished') : t('listing-card--inquiry-sent')}
              classNameModifier={classNames({
                popup: isPopup,
                full: isUnpublished,
                offset: !isUnpublished,
              })}
            />
          )}
          {img}
        </div>
        <ListingCardOverview
          heading={heading}
          listingId={listingId}
          guests={capacity}
          isPopup={isPopup}
          isHighlighted={isHighlighted}
          cardLocation={cardLocation}
          withFavorite={withFavorite}
        />
        <h3 className={getClassNames(styles, 'title', modifiers)}>{headline}</h3>
        <div className={getClassNames(styles, 'details', classNames(modifiers))}>
          {!isUnpublished && (
            <span>
              <ListingBadge
                variant="rating"
                isPopup={isPopup}
              >
                {rating ? rating.toFixed(1) : t('listing-card--new')}
              </ListingBadge>
              {isSuperOwner && (
                <ListingBadge
                  variant="superowner"
                  isPopup={isPopup}
                >
                  {t('listing-card--listing-badge--superowner')}
                </ListingBadge>
              )}
              {bestOfYear && (
                <ListingBadge
                  variant="bestOfYear"
                  isPopup={isPopup}
                >
                  {t('listing-card--listing-badge--best-of-year', { year: bestOfYear })}
                </ListingBadge>
              )}
            </span>
          )}
          <span className={styles.pricingDetails}>
            {!snoozeActive && searchPricing?.price_unit && (
              <div className={getClassNames(styles, 'pricing', modifiers)}>
                <Trans
                  t={t}
                  i18nKey={`price-details--${searchPricing.price_unit}-price`}
                  values={{ price: searchPricing?.formatted }}
                  components={[
                    <span className={getClassNames(styles, 'pricingFormatted', classNames({ popup: variant === 'popup' }))} />,
                    <span className={getClassNames(styles, 'pricingUnit', classNames({ popup: variant === 'popup' }))} />,
                  ]}
                />
              </div>
            )}
            {!snoozeActive && instantBookingAvailable && (
              <ListingBadge
                variant="instabook"
                isPopup={isPopup}
              >
                {t('listing-card--listing-badge--instabook')}
              </ListingBadge>
            )}
            {snoozeActive && (
              <ListingBadge
                variant="snoozed"
                isPopup={isPopup}
              >
                {t('listing-card--snoozed', { endDate: formattedSnoozeEndDate })}
              </ListingBadge>
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ListingCard;
